import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Car, Pencil, Trash2, Route as RouteIcon } from "lucide-react";
import { PageHeader, TypeBadge, EmptyState, ConfirmDelete } from "@/components/Shared";
import { TripDialog } from "@/components/TripDialog";
import { DateFilters } from "@/components/DateFilters";
import { api, errMsg, fmtDate, fmtMiles } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

const TYPES = [
  { v: "all", label: "All" },
  { v: "business", label: "Business" },
  { v: "personal", label: "Personal" },
];

const RowActions = ({ onEdit, onDelete, id }) => (
  <div className="flex items-center gap-1 justify-end">
    <button onClick={onEdit} className="h-10 w-10 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100" aria-label="Edit" data-testid={`trip-edit-${id}`}>
      <Pencil className="w-4 h-4" />
    </button>
    <button onClick={onDelete} className="h-10 w-10 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50" aria-label="Delete" data-testid={`trip-delete-${id}`}>
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

const TripTable = ({ trips, onEdit, onDelete }) => (
  <div className="mt-card overflow-hidden hidden md:block" data-testid="trips-table">
    <table className="w-full text-left">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr className="mt-overline">
          <th className="px-5 py-3.5">Date</th>
          <th className="px-5 py-3.5 text-right">Miles</th>
          <th className="px-5 py-3.5">Type</th>
          <th className="px-5 py-3.5">Purpose</th>
          <th className="px-5 py-3.5">Client / Job</th>
          <th className="px-5 py-3.5 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {trips.map((t) => (
          <tr key={t.id} className="hover:bg-slate-50/70 transition-colors" data-testid={`trip-row-${t.id}`}>
            <td className="px-5 py-4 font-medium text-slate-800 whitespace-nowrap">{fmtDate(t.date)}</td>
            <td className="px-5 py-4 text-right font-mono font-bold text-slate-900">{fmtMiles(t.miles)}</td>
            <td className="px-5 py-4"><TypeBadge type={t.trip_type} /></td>
            <td className="px-5 py-4 text-slate-700 max-w-[260px] truncate">{t.purpose || "—"}</td>
            <td className="px-5 py-4 text-slate-700 max-w-[200px] truncate">{t.client || "—"}</td>
            <td className="px-5 py-2"><RowActions id={t.id} onEdit={() => onEdit(t)} onDelete={() => onDelete(t)} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TripCards = ({ trips, onEdit, onDelete }) => (
  <div className="md:hidden space-y-3" data-testid="trips-cards">
    {trips.map((t) => (
      <div key={t.id} className="mt-card p-4" data-testid={`trip-card-${t.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-slate-500">{fmtDate(t.date)}</p>
            <p className="font-semibold text-slate-900 truncate mt-0.5">{t.purpose || "Trip"}</p>
            {t.client && <p className="text-sm text-slate-600 truncate">{t.client}</p>}
          </div>
          <p className="font-mono text-2xl font-bold text-slate-900 shrink-0">{fmtMiles(t.miles)}<span className="text-sm text-slate-500 ml-1">mi</span></p>
        </div>
        <div className="flex items-center justify-between mt-3">
          <TypeBadge type={t.trip_type} />
          <RowActions id={`m-${t.id}`} onEdit={() => onEdit(t)} onDelete={() => onDelete(t)} />
        </div>
      </div>
    ))}
  </div>
);

export default function MileageLog() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [type, setType] = useState("all");
  const [trips, setTrips] = useState(null);
  const { requirePaid } = useSubscription();
  const [dialog, setDialog] = useState({ open: false, trip: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    const params = { start: range.start || undefined, end: range.end || undefined, trip_type: type === "all" ? undefined : type };
    return api.get("/trips", { params }).then(({ data }) => setTrips(data));
  }, [range, type]);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try {
      await api.delete(`/trips/${toDelete.id}`);
      toast.success("Trip deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  const total = (trips || []).reduce((s, t) => s + t.miles, 0);
  const biz = (trips || []).filter((t) => t.trip_type === "business").reduce((s, t) => s + t.miles, 0);

  return (
    <div data-testid="mileage-log-page">
      <PageHeader overline="Records" title="Mileage Log">
        <button onClick={requirePaid(() => setDialog({ open: true, trip: null }))} className="mt-btn-primary" data-testid="mileage-add-trip-button">
          <Car className="w-5 h-5" /> Track a Trip
        </button>
      </PageHeader>
      <div className="mt-card p-4 sm:p-5 mb-5 grid lg:grid-cols-[1fr_auto] gap-5 items-end">
        <DateFilters start={range.start} end={range.end} onChange={setRange} />
        <div>
          <p className="mt-overline mb-1.5">Trip type</p>
          <div className="inline-flex rounded-xl bg-slate-100 p-1" data-testid="mileage-filter-type">
            {TYPES.map((t) => (
              <button key={t.v} onClick={() => setType(t.v)} data-testid={`mileage-filter-type-${t.v}`}
                className={`h-10 px-4 rounded-lg text-sm font-semibold transition-colors ${type === t.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {trips && trips.length > 0 && (
        <div className="flex flex-wrap gap-x-8 gap-y-2 mb-4 px-1" data-testid="mileage-totals">
          <p className="text-slate-600"><span className="font-mono font-bold text-slate-900 text-lg" data-testid="mileage-total-miles">{fmtMiles(total)}</span> total miles</p>
          <p className="text-slate-600"><span className="font-mono font-bold text-emerald-700 text-lg" data-testid="mileage-business-miles">{fmtMiles(biz)}</span> business miles</p>
          <p className="text-slate-600"><span className="font-bold text-slate-900">{trips.length}</span> trips</p>
        </div>
      )}
      {trips === null ? (
        <div className="h-48 mt-card animate-pulse" />
      ) : trips.length === 0 ? (
        <EmptyState icon={RouteIcon} title="No trips found" text="Add your first trip, or change the filters above." testId="mileage-empty" />
      ) : (
        <>
          <TripTable trips={trips} onEdit={(t) => setDialog({ open: true, trip: t })} onDelete={setToDelete} />
          <TripCards trips={trips} onEdit={(t) => setDialog({ open: true, trip: t })} onDelete={setToDelete} />
        </>
      )}
      <TripDialog open={dialog.open} trip={dialog.trip} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
      <ConfirmDelete open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete this trip?" onConfirm={doDelete} />
    </div>
  );
}
